import { Card, ComboboxData, ComboboxItem, Grid, Loader, Select, Title } from "@mantine/core";
import { issuePriortyEnums, TIssueStatus } from "@mctypes/global.types";
import { IIssueBoardApi } from "@mcapps/shared";
import { IIssueApi, IIssueUpdateSchemaProps, IIssueUpdateUserSchemaProps, IUserPartial } from "@schemas/issueSchema";
import { IconExternalLink } from "@tabler/icons-react";
import { EditorState } from "lexical/LexicalEditorState";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetProjectMembersQuery,
  usePutProjectByProjectIdIssueUpdateDescriptionMutation,
  usePutProjectByProjectIdIssueUpdateSimpleMutation,
  usePutProjectByProjectIdIssueUpdateUserMutation,
} from "../../../../../../../openapi";
import { McTitle } from "../../../../../../components";
import FoldableSection from "../../../../../../components/FoldableSection/FoldableSection";
import { McAutocomplete } from "../../../../../../components/McAutocomplete";
import { McColorSelect } from "../../../../../../components/McColorSelect";
import McInputNew from "../../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../../components/McInput/McTextAreaNew";
import { McMantineMultiUserSelect } from "../../../../../../components/McMantineUserSelect/McMantineUserMultiSelect";
import { McMantineUserSelect } from "../../../../../../components/McMantineUserSelect/McMantineUserSelect";
import RichTextEditor, {
  lexicalStateToPlainText,
  plainTextToLexicalState,
} from "../../../../../../components/RichTextEditorMantine/RichTextEditor";
import { SimpleTagsMantine } from "../../../../../../components/SimpleTags/SimpleTagsMantine";
import { useAppSelector } from "../../../../../../hooks";
import { IssueDisabledColumnsEnum } from "@mcapps/shared/enums";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { formatTags, sortArrayByNumber } from "../../../../../utils";
import { ISSUE_PRIORTY_OPTIONS, ISSUE_STATES_OPTIONS } from "../../../issueConstants";
import { useGetUniqueTagsOnBoardQuery } from "../../../issueRTKLegacy";
import { getIssueBoardScopedMembers, selectTypeAheadPNSItems } from "../../../issueSelectors";
import { IssueConnectedBimSpacesSection } from "./IssueConnectedBimSpacesSection";
import {
  CellCtx,
  CellDef,
  createDescriptionPayload,
  createSimplePayload,
  createUserPayload,
  guardedIssueUpdate,
} from "./modalUtils";

const CELL_DEFS: CellDef[] = [
  {
    id: "owner",
    disableKey: IssueDisabledColumnsEnum.OWNER,
    span: 4,
    render: (ctx) => (
      <McTitle title="Eier">
        <McMantineUserSelect
          value={ctx.issue.owner?._id}
          data={ctx.usersComboboxData}
          onChange={(v) =>
            ctx.updateUser({ owner: v == null ? null : ctx.multiUsersComboboxData.find((u) => u._id === v) })
          }
        />
      </McTitle>
    ),
  },
  {
    id: "assignedTo",
    disableKey: IssueDisabledColumnsEnum.ASSIGNEDTO,
    span: 4,
    render: (ctx) => (
      <McTitle title="Ansv. for utførelse">
        <McMantineUserSelect
          value={ctx.issue.assignedTo?._id}
          data={ctx.usersComboboxData}
          onChange={(v) =>
            ctx.updateUser({ assignedTo: v == null ? null : ctx.multiUsersComboboxData.find((u) => u._id === v) })
          }
        />
      </McTitle>
    ),
  },
  {
    id: "members",
    disableKey: IssueDisabledColumnsEnum.ITEMMEMBERS,
    span: 4,
    render: (ctx) => (
      <McTitle title="Medlemmer">
        <McMantineMultiUserSelect
          defaultValue={ctx.issue.itemMembers || []}
          data={ctx.multiUsersComboboxData}
          onChange={(v) => ctx.updateUser({ itemMembers: v })}
        />
      </McTitle>
    ),
  },
  {
    id: "status",
    disableKey: IssueDisabledColumnsEnum.STATUS,
    span: 4,
    render: (ctx) => (
      <McTitle title="Status">
        <McColorSelect
          onChange={(newValue) => ctx.updateSimple({ status: newValue as TIssueStatus })}
          data={ISSUE_STATES_OPTIONS.map((s) => ({
            key: s.value,
            displayName: s.value,
            color: s.color,
          }))}
          value={ctx.issue.status}
        />
      </McTitle>
    ),
  },
  {
    id: "priority",
    disableKey: IssueDisabledColumnsEnum.PRIORITY,
    span: 4,
    render: (ctx) => (
      <McTitle title="Prioritet">
        <McColorSelect
          onChange={(newValue) => ctx.updateSimple({ priority: newValue as issuePriortyEnums })}
          data={ISSUE_PRIORTY_OPTIONS}
          value={ctx.issue.priority}
        />
      </McTitle>
    ),
  },
  {
    id: "referenceUrl",
    disableKey: IssueDisabledColumnsEnum.REFERENCEURL,
    span: 4,
    render: (ctx) => (
      <McTitle title="Link">
        <McTitle.InlineData positionInlineData="flex-end">
          {/https?:\/\/.*/i.test(ctx.issue.referenceUrl) === true && (
            <a
              style={{ display: "flex", color: "var(--mantine-color-blue-4)" }}
              href={ctx.issue.referenceUrl}
              target="_blank"
              rel="noreferrer"
            >
              <IconExternalLink size={16} />
            </a>
          )}
        </McTitle.InlineData>

        <McInputNew
          placeholder="http://"
          onBlur={(e) => ctx.updateSimple({ referenceUrl: e.target.value })}
          value={ctx.issue.referenceUrl}
        />
      </McTitle>
    ),
  },
  {
    id: "bucket",
    disableKey: IssueDisabledColumnsEnum.BUCKET,
    span: 4,
    render: (ctx) => (
      <McTitle title={ctx.issueBoard?.bucketNameSetting || ""}>
        <Select
          allowDeselect={false}
          value={ctx.issue.bucket}
          data={ctx.bucketOptions}
          onChange={(newValue) => ctx.updateSimple({ bucket: newValue })}
          disabled={ctx.issue.taskOrIssue === "TASK" || ctx.bucketReadOnly}
        />
      </McTitle>
    ),
  },
  {
    id: "typeOfIssue",
    disableKey: IssueDisabledColumnsEnum.TYPEOFISSUE,
    span: 4,
    render: (ctx) => (
      <McTitle title="Type">
        <Select
          allowDeselect={false}
          value={ctx.issue.typeOfIssue}
          data={ctx.typeOptions}
          onChange={(newValue) => ctx.updateSimple({ typeOfIssue: newValue })}
        />
      </McTitle>
    ),
  },
  {
    id: "pns",
    disableKey: IssueDisabledColumnsEnum.PNS,
    span: 4,
    render: (ctx) => (
      <McTitle title="PNS">
        <LocalErrorBoundary id="IssuePnsDropdown">
          <McAutocomplete
            clearable={true}
            onClear={() => ctx.updateSimple({ pns: "" })}
            data={ctx.typeAheadPNSItems}
            value={ctx.issue.pns}
            onCommit={(e) => ctx.updateSimple({ pns: e })}
          />
        </LocalErrorBoundary>
      </McTitle>
    ),
  },
  {
    id: "description",
    disableKey: IssueDisabledColumnsEnum.DESCRIPTION,
    span: 12,
    render: (ctx) => (
      <McTitle title="Beskrivelse">
        <IssueDescriptionRichTextField ctx={ctx} />
      </McTitle>
    ),
  },
  {
    id: "conclusion",
    disableKey: IssueDisabledColumnsEnum.CONCLUSION,
    span: 12,
    when: (ctx) =>
      (ctx.issue.status === "Fullført" && ctx.issueBoard.showConclusion === true) ||
      ctx.issueBoard.showConclusion === false,
    render: (ctx) => (
      <McTitle title="Konklusjon">
        <McTextAreaNew
          value={ctx.issue.conclusion}
          onBlur={(e) => ctx.updateSimple({ conclusion: e.target.value })}
          autosize
        />
      </McTitle>
    ),
  },
  {
    id: "tags",
    span: 12,
    render: (ctx) => (
      <McTitle title="Etiketter">
        <SimpleTagsMantine
          activeTags={ctx.issue.tags}
          tagOptions={ctx.issueTags}
          allowNew={ctx.issueBoard.useOnlyPredefinedTags !== true}
          onChange={async (value) => await ctx.updateSimple({ tags: value })}
        />
      </McTitle>
    ),
  },
  {
    id: "connectedBimSpaces",
    disableKey: IssueDisabledColumnsEnum.CONNECTEDBIMSPACES,
    span: 12,
    render: (ctx) => <IssueConnectedBimSpacesSection issue={ctx.issue} projectId={ctx.projectId} />,
  },
];

export const IssueMainPropertiesSection: React.FC<{
  issue: IIssueApi;
  issueBoard?: IIssueBoardApi;
  projectId: string;
}> = ({ issue, issueBoard, projectId }) => {
  if (issue == null || issueBoard == null) {
    return <Loader />;
  }

  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const [updateDescriptionValue] = usePutProjectByProjectIdIssueUpdateDescriptionMutation();
  const [updateUserValue] = usePutProjectByProjectIdIssueUpdateUserMutation();
  const buildSimplePayload = useMemo(() => createSimplePayload(projectId, issue._id), [projectId, issue._id]);
  const buildDescriptionPayload = useMemo(() => createDescriptionPayload(projectId, issue._id), [projectId, issue._id]);
  const buildUserPayload = useMemo(() => createUserPayload(projectId, issue._id), [projectId, issue._id]);

  const updateSimple = useCallback(
    async (prop: IIssueUpdateSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateValue,
        buildPayload: buildSimplePayload,
      });
    },
    [issue, updateValue, buildSimplePayload],
  );

  const updateUser = useCallback(
    async (prop: IIssueUpdateUserSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateUserValue,
        buildPayload: buildUserPayload,
      });
    },
    [issue, updateUserValue, buildUserPayload],
  );

  const updateDescription = useCallback(
    async (prop: { description: string; descriptionRichText: string }) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateDescriptionValue,
        buildPayload: buildDescriptionPayload,
      });
    },
    [issue, updateDescriptionValue, buildDescriptionPayload],
  );

  const issueBoardBuckets = useMemo(() => sortArrayByNumber(issueBoard?.buckets ?? [], "order"), [issueBoard?.buckets]);
  const typeAheadPNSItems = useAppSelector(selectTypeAheadPNSItems);
  const { data: projectMembers = [] } = useGetProjectMembersQuery({ projectId }, { skip: projectId == null });
  const projectUsers = useMemo(
    () => getIssueBoardScopedMembers(projectMembers, issueBoard),
    [issueBoard, projectMembers],
  );

  const selectableUsers: IUserPartial[] = useMemo(
    () =>
      projectUsers.reduce<IUserPartial[]>((acc, member) => {
        const user = member.user;
        if (user != null && typeof user !== "string") {
          acc.push({ _id: user._id, name: user.name });
        }
        return acc;
      }, []),
    [projectUsers],
  );

  // Remove and use multiUsersComboboxData when custom SingleUserSelect is created
  const usersComboboxData: ComboboxItem[] = useMemo(
    () =>
      selectableUsers.map((user) => ({
        label: user.name,
        value: user._id,
      })),
    [selectableUsers],
  );

  const multiUsersComboboxData: IUserPartial[] = useMemo(
    () =>
      selectableUsers.map((user) => ({
        _id: user._id,
        name: user.name,
      })),
    [selectableUsers],
  );

  const bucketOptions: ComboboxData = useMemo(() => {
    const options = issueBoardBuckets.map((b) => ({ value: b.value, label: b.value }));

    if (
      issue.bucket != null &&
      issue.bucket !== "" &&
      options.some((option) => option.value === issue.bucket) === false
    ) {
      return [{ value: issue.bucket, label: issue.bucket }, ...options];
    }

    return options;
  }, [issue.bucket, issueBoardBuckets]);

  const bucketReadOnly = useMemo(
    () =>
      issue.bucket != null &&
      issue.bucket !== "" &&
      issueBoardBuckets.some((bucket) => bucket.value === issue.bucket) === false,
    [issue.bucket, issueBoardBuckets],
  );

  const typeOptions: ComboboxData = useMemo(
    () => (issueBoard?.types ?? []).map((t) => ({ value: t.name, label: t.name })),
    [issueBoard?.types],
  );

  const { data: tags, isLoading: tagsLoading } = useGetUniqueTagsOnBoardQuery({
    projectId: projectId,
    boardId: issueBoard._id,
  });

  const issueTags = formatTags(tags);
  const disabledSet = useMemo(() => new Set(issueBoard?.disabledColumns ?? []), [issueBoard?.disabledColumns]);

  const ctx: CellCtx = useMemo(
    () => ({
      issue,
      issueBoard,
      projectId,
      usersComboboxData,
      multiUsersComboboxData,
      bucketOptions,
      bucketReadOnly,
      typeOptions,
      typeAheadPNSItems,
      issueTags,
      tagsLoading,
      projectMembers: projectUsers,
      updateSimple,
      updateDescription,
      updateUser,
    }),
    [
      issue,
      issueBoard,
      projectId,
      usersComboboxData,
      multiUsersComboboxData,
      bucketOptions,
      bucketReadOnly,
      typeOptions,
      typeAheadPNSItems,
      issueTags,
      tagsLoading,
      projectUsers,
      updateSimple,
      updateDescription,
      updateUser,
    ],
  );

  const visibleCells = useMemo(() => {
    return CELL_DEFS.filter((cell) => {
      // if the cell is disableable, check it
      if (cell.disableKey != null && disabledSet.has(cell.disableKey)) return false;
      return true;
    }).filter((cell) => (cell.when ? cell.when(ctx) : true));
  }, [disabledSet, ctx]);

  return (
    <FoldableSection storageKey="issue-modal-properties">
      <FoldableSection.Header>
        <Title order={6}>Detaljer</Title>
      </FoldableSection.Header>

      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Grid flex={1}>
            {visibleCells.map((cell) => (
              <Grid.Col key={cell.id} span={typeof cell.span === "number" ? cell.span : cell.span(ctx)}>
                {cell.render(ctx)}
              </Grid.Col>
            ))}
          </Grid>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};

const IssueDescriptionRichTextField: React.FC<{ ctx: CellCtx }> = ({ ctx }) => {
  const [richTextTree, setRichTextTree] = useState("");
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [plainText, setPlainText] = useState(ctx.issue.description ?? "");
  const [isDirty, setIsDirty] = useState(false);
  const [isPreparing, setIsPreparing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const savedPlainTextRef = useRef(ctx.issue.description ?? "");
  const savedRichTextRef = useRef("");

  useEffect(() => {
    let isMounted = true;

    const prepareEditorState = async () => {
      setIsPreparing(true);
      setEditorState(null);

      const nextPlainText = ctx.issue.description ?? "";
      const nextRichText =
        ctx.issue.descriptionRichText != null && ctx.issue.descriptionRichText.trim().length > 0
          ? ctx.issue.descriptionRichText
          : await plainTextToLexicalState(nextPlainText);

      if (!isMounted) {
        return;
      }

      savedPlainTextRef.current = nextPlainText;
      savedRichTextRef.current = nextRichText;
      setPlainText(nextPlainText);
      setRichTextTree(nextRichText);
      setIsDirty(false);
      setEditorKey((key) => key + 1);
      setIsPreparing(false);
    };

    void prepareEditorState();

    return () => {
      isMounted = false;
    };
  }, [ctx.issue._id, ctx.issue.description, ctx.issue.descriptionRichText]);

  const handleChange = useCallback((nextEditorState: EditorState, nextPlainText: string) => {
    const nextRichText = JSON.stringify(nextEditorState);

    setEditorState(nextEditorState);
    setPlainText(nextPlainText);
    setIsDirty(nextPlainText !== savedPlainTextRef.current || nextRichText !== savedRichTextRef.current);
  }, []);

  const save = useCallback(async () => {
    if (!isDirty || isSaving) {
      return;
    }

    const nextRichText = editorState != null ? JSON.stringify(editorState) : richTextTree;
    const nextPlainText = editorState != null ? await lexicalStateToPlainText(editorState) : plainText;

    setIsSaving(true);
    try {
      await ctx.updateDescription({
        description: nextPlainText,
        descriptionRichText: nextRichText,
      });
      savedPlainTextRef.current = nextPlainText;
      savedRichTextRef.current = nextRichText;
      setIsDirty(false);
    } finally {
      setIsSaving(false);
    }
  }, [ctx, editorState, isDirty, isSaving, plainText, richTextTree]);

  if (isPreparing) {
    return <Loader size="sm" />;
  }

  return (
    <>
      <RichTextEditor
        key={editorKey}
        richTextTree={richTextTree}
        members={ctx.projectMembers ?? []}
        handleMentions={() => undefined}
        onChange={handleChange}
        onBlur={save}
        disabled={isSaving}
        minRows={4}
        maxRows={16}
      />
    </>
  );
};
